import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewService } from '@/services/reviewService';
import { toReviewError, type ReviewError } from '../utils/errorMap';
import type { SubmissionRow } from '../types/submission';

interface UseSubmissionReturn {
  row: SubmissionRow | null;
  loading: boolean;
  error: ReviewError | null;
  refetch: () => void;
}

// Short so drawer/edit-dialog double-mounts dedupe but re-opening after a PATCH refetches.
const SUBMISSION_STALE_MS = 5 * 1000;

export function useSubmission(
  resource: string | undefined,
  id: number | string | undefined,
): UseSubmissionReturn {
  const queryClient = useQueryClient();
  const enabled = Boolean(resource) && id !== undefined && id !== null && id !== '';
  const query = useQuery<SubmissionRow | null, unknown>({
    queryKey: ['impact-data', 'submission', resource, id],
    // Normalise service's `undefined` (not-found) to `null` so useQuery's
    // typed `data` has a single non-Optional shape.
    queryFn: async () => {
      const row = await reviewService.getSubmission(
        resource as string,
        id as number | string,
      );
      return row ?? null;
    },
    enabled,
    staleTime: SUBMISSION_STALE_MS,
  });

  const refetch = useCallback(() => {
    if (!enabled) return;
    queryClient.invalidateQueries({
      queryKey: ['impact-data', 'submission', resource, id],
    });
  }, [enabled, queryClient, resource, id]);

  return {
    row: query.data ?? null,
    loading: query.isPending && enabled,
    error: query.error ? toReviewError(query.error) : null,
    refetch,
  };
}
